import { Component, inject } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import {SessionService} from '@shared/services/session.service';
import {debounceTime} from 'rxjs/operators';
import {AddressType} from '@app/relation/shared/address-type';
import {AddressTypeApi} from '@app/relation/shared/address-type.api';
import {CommunicationTypeApi} from '@app/relation/shared/communication-type.api';
import {CommunicationType} from '@app/relation/shared/communication-type';
import {IsoCountry} from '@shared/objects/iso-country';
import {IsoApi} from '@shared/api/iso.api';
import {InfoApi} from '@shared/api/info.api';
import { ToastrService } from 'ngx-toastr';
import {RelationApi} from '@app/relation/shared/relation.api';
import {MatDialogRef} from '@angular/material/dialog';
import { MatToolbar } from '@angular/material/toolbar';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/autocomplete';
import { MatButton } from '@angular/material/button';

@Component({
    templateUrl: 'create-relation-dialog.component.html',
    styleUrls: ['create-relation-dialog.component.scss'],
    imports: [MatToolbar, ReactiveFormsModule, MatFormField, MatLabel, MatInput, MatSelect, MatOption, MatButton]
})
export class CreateRelationDialogComponent {
  private dialogRef = inject<MatDialogRef<CreateRelationDialogComponent>>(MatDialogRef);
  private formBuilder = inject(UntypedFormBuilder);
  private infoApi = inject(InfoApi);
  private relationApi = inject(RelationApi);
  private sessionService = inject(SessionService);
  private toastrService = inject(ToastrService);

  title = 'Relatie aanmaken';

  countries: IsoCountry[];
  relationForm: UntypedFormGroup;

  addressType: AddressType;
  communicationType: CommunicationType;

  constructor() {
    const addressTypeApi = inject(AddressTypeApi);
    const communicationTypeApi = inject(CommunicationTypeApi);
    const isoApi = inject(IsoApi);


    this.createForm();

    isoApi.countries().subscribe(countries => {
      this.countries = countries;
    });

    addressTypeApi.findAll().subscribe(value => {
      const addressType = value.find(value1 => value1.name.toLowerCase() === 'post');
      if (addressType) {
        this.addressType = addressType;
      } else {
        this.addressType = value[0];
      }
      this.createAddress();
    });

    communicationTypeApi.findAll().subscribe(value => {
      const communicationType = value.find(value1 => value1.name.toLowerCase() === 'email');
      if (communicationType) {
        this.communicationType = communicationType;
      } else {
        this.communicationType = value[0];
      }
      this.createCommunication();
    });
  }

  createForm() {
    // use the FormBuilder to create a FormGroup
    this.relationForm = this.formBuilder.group({
      id: null,
      number: null,
      title: null,
      firstname: null,
      infix: null,
      familyname: null,
      companyname: null,
      vatnumber: null,
      comment: null,
      relationGroupNumber: null,
      addresses: this.formBuilder.array([]),
      communications: this.formBuilder.array([]),
      active: true
    });
  }

  createCommunication() {
    const communication = this.formBuilder.group({
      id: null,
      typeId: [this.communicationType.id, Validators.required],
      invoice: [true],
      reminder: [true],
      standard: [true],
      value: [null, Validators.required]
    });

    const communications = this.relationForm.get('communications') as UntypedFormArray;
    communications.push(communication);
  }

  createAddress() {
    const address = this.formBuilder.group({
      id: null,
      typeId: [this.addressType.id, Validators.required],
      street: [null, Validators.required],
      housenumber: [null, Validators.required],
      zip: [null, Validators.required],
      city: [null, Validators.required],
      isoCountryId: [this.sessionService.session.administration.isoCountryId, Validators.required],
      standard: [true],
    });

    const addresses = this.relationForm.get('addresses') as UntypedFormArray;
    addresses.push(address);

    address.controls['zip'].valueChanges
      .pipe(debounceTime(500))
      .subscribe(() => this.onLookupAddress(address));

    address.controls['housenumber'].valueChanges
      .pipe(debounceTime(500))
      .subscribe(() => this.onLookupAddress(address));
  }

  onLookupAddress(address: UntypedFormGroup) {
    if (address.value.zip && address.value.housenumber) {
      this.infoApi.bag(address.value.zip.replace(' ', ''), address.value.housenumber).subscribe(value => {
        if (value && value.length > 0) {
          address.patchValue({
            street: value[0].street,
            city: value[0].city
          }, {emitEvent: false});
        } else {
          const message = `Adres niet gevonden voor ${address.value.zip} en ${address.value.housenumber}.`;
          this.toastrService.info(message, 'Adres niet gevonden');
        }
      }, (err) => console.log(err));
    }
  }

  onClose() {
    this.dialogRef.close();
  }

  onSend() {
    this.relationApi.save(this.relationForm.value).subscribe(value => {
      this.dialogRef.close(value);
    });
  }
}
